import { useEffect, useRef, useState } from 'react';

import { env } from '../../lib/env';

/** Cloudflare's widget API, as much of it as we use. */
interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      'expired-callback': () => void;
      'error-callback': () => void;
      theme?: 'light' | 'dark' | 'auto';
    },
  ) => string;
  remove: (id: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptPromise: Promise<void> | null = null;

/** Loaded once per document, however many widgets ask for it. */
function loadScript(): Promise<void> {
  if (scriptPromise !== null) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`);
    if (existing !== null) {
      resolve();
      return;
    }
    const el = document.createElement('script');
    el.src = SCRIPT_SRC;
    el.async = true;
    el.defer = true;
    el.onload = () => {
      resolve();
    };
    el.onerror = () => {
      reject(new Error('Turnstile failed to load'));
    };
    document.head.appendChild(el);
  });
  return scriptPromise;
}

interface Props {
  /** Called with a fresh token, and with null when one expires or errors. */
  onToken: (token: string | null) => void;
}

/**
 * The bot gate on signup and the institute application.
 *
 * OPTIONAL BY CONSTRUCTION. With no `VITE_TURNSTILE_SITE_KEY` this renders
 * nothing at all and the form works exactly as it did — which is the only
 * responsible default, because shipping a required gate that nobody has keys
 * for yet would take signup down.
 *
 * It is also not the thing holding the door. `request-institute` triggers real
 * email at a stranger's address, so it is throttled in the database whatever
 * happens here; this widget raises the cost of the first attempt, and
 * auth_throttle_hit() bounds the rest.
 */
export function Turnstile({ onToken }: Props): JSX.Element | null {
  const host = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  const siteKey = env.TURNSTILE_SITE_KEY;

  useEffect(() => {
    if (siteKey === null) return undefined;
    let widgetId: string | null = null;
    let cancelled = false;

    loadScript()
      .then(() => {
        if (cancelled || host.current === null || window.turnstile === undefined) return;
        widgetId = window.turnstile.render(host.current, {
          sitekey: siteKey,
          theme: 'light',
          callback: (token) => {
            onToken(token);
          },
          'expired-callback': () => {
            onToken(null);
          },
          'error-callback': () => {
            onToken(null);
          },
        });
      })
      .catch(() => {
        if (cancelled) return;
        // The script is blocked or Cloudflare is down. Say so rather than
        // leaving a silent gap where a widget should be, and let the server's
        // throttle carry the load.
        setFailed(true);
        onToken(null);
      });

    return () => {
      cancelled = true;
      if (widgetId !== null && window.turnstile !== undefined) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [siteKey, onToken]);

  if (siteKey === null) return null;

  return (
    <div>
      <div ref={host} />
      {failed ? (
        <p className="text-[length:var(--text-xs)] text-[color:var(--text-muted)]">
          The verification widget could not load. You can still continue.
        </p>
      ) : null}
    </div>
  );
}
